const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/pages/ErrorPage/ErrorPage.tsx';
import { Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { css } from '@emotion/css';
import { colors } from '../../defs/colors';
import { useIntl } from 'react-intl';
import textMap from './translations';
import { ErrorPageLayout } from './ErrorPageLayout';
import { ReactNode, useMemo } from 'react';

interface Props {
    icon: ReactNode;
    error: Error;
    header: string;
    summary: string;
    hideRestartBtn?: boolean;
}

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            button: css`
                color: ${theme.palette.getContrastText(theme.palette.error.main)};
                background-color: ${theme.palette.error.main};

                &:hover {
                    background-color: ${colors.red['500']};
                }
            `,
        }),
        [theme]
    );
}

export const ErrorPage = (props: Props) => {
    const { formatMessage } = useIntl();
    const classes = useCSSClasses();

    const devBuild = process.env.NODE_ENV !== 'production';

    const header = (
        <Typography variant={'h5'} component={'div'} gutterBottom={true}>
            {props.header}
        </Typography>
    );

    const summary = (
        <Typography variant={'body1'} component={'div'}>
            {props?.summary}
        </Typography>
    );

    let errorCode: string;
    let errorInformation: ReactNode | undefined = undefined;

    if (devBuild) {
        errorCode = `${props.error.name}${props.error.name && props.error.message ? ': ' : ''}${props.error.message}`;
        errorInformation = (
            <Typography align={'left'} variant={'overline'} component={'div'}>
                {errorCode}
            </Typography>
        );
    }

    const restartBtn = !props.hideRestartBtn && (
        <Button
            variant="contained"
            size="large"
            color="inherit"
            fullWidth={true}
            onClick={() => {
                document.location.href = `${document.location.protocol}//${document.location.host}/#/`;
                document.location.reload();
            }}
            classes={{ contained: classes.button }}
        >
            {formatMessage(textMap.reload)}
        </Button>
    );

    return (
        <ErrorPageLayout
            icon={props.icon}
            header={header}
            summary={summary}
            errorInformation={errorInformation}
            restartBtn={restartBtn || undefined}
        />
    );
};
